/**
 * Decides whether the document we were served can be hydrated.
 *
 * scripts/prerender.js writes a real HTML snapshot for every route in the SEO
 * matrix, but CloudFront also maps any unmatched path to `/index.html` (the
 * homepage snapshot) with a 200 — see the errorResponses block in
 * localingos-infrastructure/src/stack/frontend-stack.ts. So the served markup
 * belongs to the requested route only sometimes, and hydrating unconditionally
 * would mean React trying to reconcile, say, the dashboard against the homepage.
 *
 * The prerenderer stamps the path it rendered onto the root element and we hydrate
 * only on an exact match. The stamp is written by the build, never by the app, so
 * a client-rendered document can never mistake itself for a prerendered one.
 */

/** Attribute scripts/prerender.js sets on #root before serialising the DOM. */
export const PRERENDERED_PATH_ATTRIBUTE = 'data-prerendered-path';

/**
 * Trailing slashes are routing-equivalent but not string-equivalent, and
 * CloudFront serves both `/pricing` and `/pricing/` from the same object.
 */
function normalisePath(pathname: string): string {
    if (!pathname) return '/';
    const trimmed = pathname.replace(/\/+$/, '');
    return trimmed === '' ? '/' : trimmed;
}

/** The path this document was prerendered for, or null if it was not prerendered. */
export function getPrerenderedPath(container: Element): string | null {
    return container.getAttribute(PRERENDERED_PATH_ATTRIBUTE);
}

/**
 * True when `container` holds prerendered markup for the path currently in the
 * address bar, so React can adopt that DOM rather than rebuild it.
 */
export function canHydrate(container: Element, pathname: string): boolean {
    const prerendered = getPrerenderedPath(container);
    if (prerendered === null) return false;
    return normalisePath(prerendered) === normalisePath(pathname);
}

/**
 * Whether this page load will hydrate. Callers that must produce markup matching
 * the snapshot — i18n's initial language, for one — need to know before rendering.
 */
export function willHydrate(): boolean {
    if (typeof document === 'undefined') return false;
    const container = document.getElementById('root');
    return !!container && canHydrate(container, window.location.pathname);
}
